// Durable write-through queue for PouchDB shadow-writes.
//
// Domain mutators (store/index.ts) optimistically update Zustand, then
// write-through to PouchDB. Those writes can fail (stale handles after a
// reconnect, validation, storage quota). Previously the failure was swallowed
// — the UI showed success while the record silently never persisted.
//
// runShadow keeps the failed write in a pending map and retries it on a timer,
// on `online`, and on tab focus, so a transient failure self-heals instead of
// losing data. Callers surface the first failure to the user via the injected
// error handler.

type Thunk = () => Promise<unknown>;

const pending = new Map<string, Thunk>();
const listeners = new Set<() => void>();
let errorHandler: ((err: unknown) => void) | null = null;
let started = false;

function notify(): void {
  for (const l of listeners) l();
}

export function pendingCount(): number {
  return pending.size;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Store injects a handler (toast) so this module stays free of store imports.
export function setSyncErrorHandler(h: (err: unknown) => void): void {
  errorHandler = h;
}

async function attempt(
  key: string,
  thunk: Thunk,
  surface: boolean
): Promise<void> {
  try {
    await thunk();
    if (pending.delete(key)) notify();
  } catch (err) {
    const wasPending = pending.has(key);
    pending.set(key, thunk); // keep newest thunk for the key (last write wins)
    if (!wasPending) notify();
    console.error('[sync] write-through failed:', key, err);
    if (surface && errorHandler) errorHandler(err);
  }
}

// Fire a write-through; on failure it is queued and retried automatically.
export function runShadow(key: string, thunk: Thunk): void {
  void attempt(key, thunk, true);
}

// Retry every queued write. Used by the auto-retry triggers; does not re-toast.
export async function flushPending(): Promise<void> {
  for (const [key, thunk] of [...pending.entries()]) {
    await attempt(key, thunk, false);
  }
}

// Idempotent: wire the auto-retry triggers once.
export function startSyncQueue(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  setInterval(() => {
    if (pending.size > 0) void flushPending();
  }, 15000);
  window.addEventListener('online', () => void flushPending());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && pending.size > 0) {
      void flushPending();
    }
  });
}
